import { useRef } from 'react';
import { Bold, Italic, Heading2, Heading3, List, Link2, Minus } from 'lucide-react';

// Renderer de um subconjunto seguro de Markdown (sem HTML injetado):
// ## / ### títulos, **negrito**, *itálico*, - listas, [texto](url), --- separador, parágrafos.

function renderInline(text, keyPrefix) {
  const parts = [];
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let m;
  let k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) {
      parts.push(<strong key={`${keyPrefix}-${k++}`} className="font-semibold text-white">{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('*')) {
      parts.push(<em key={`${keyPrefix}-${k++}`}>{tok.slice(1, -1)}</em>);
    } else {
      const lm = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (lm) {
        parts.push(
          <a key={`${keyPrefix}-${k++}`} href={lm[2]} target="_blank" rel="noopener noreferrer" className="underline decoration-zinc-600 underline-offset-4 transition-colors hover:text-white">
            {lm[1]}
          </a>
        );
      }
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export function Md({ text, className = '' }) {
  if (!text || !text.trim()) return null;
  const blocks = [];
  const lines = text.split('\n');
  let list = null;
  let k = 0;
  const flushList = () => {
    if (list) {
      blocks.push(
        <ul key={`ul-${k++}`} className="mt-3 space-y-2">
          {list.map((li, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full brand-bg" />
              <span>{renderInline(li, `li-${k}-${i}`)}</span>
            </li>
          ))}
        </ul>
      );
      list = null;
    }
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const trimmed = line.trim();
    if (trimmed.startsWith('- ')) {
      list = list || [];
      list.push(trimmed.slice(2));
      continue;
    }
    flushList();
    if (!trimmed) continue;
    if (trimmed === '---') {
      blocks.push(<hr key={`hr-${k++}`} className="my-8 border-zinc-800" />);
    } else if (trimmed.startsWith('### ')) {
      blocks.push(<h4 key={`h4-${k++}`} className="mt-8 font-heading text-lg font-bold uppercase tracking-wide text-white first:mt-0">{renderInline(trimmed.slice(4), `h4-${k}`)}</h4>);
    } else if (trimmed.startsWith('## ')) {
      blocks.push(<h3 key={`h3-${k++}`} className="mt-10 font-heading text-2xl font-extrabold uppercase tracking-tight text-white first:mt-0">{renderInline(trimmed.slice(3), `h3-${k}`)}</h3>);
    } else {
      blocks.push(<p key={`p-${k++}`} className="mt-4 leading-relaxed first:mt-0">{renderInline(trimmed, `p-${k}`)}</p>);
    }
  }
  flushList();
  return <div className={`text-zinc-300 ${className}`}>{blocks}</div>;
}

// Campo de texto com barra de formatação (escreve Markdown)
export function MarkdownField({ label, value, onChange, rows = 5, testId }) {
  const ref = useRef(null);
  const wrap = (before, after = before) => {
    const el = ref.current;
    if (!el) return;
    const { selectionStart: s, selectionEnd: e } = el;
    const sel = value.slice(s, e) || 'texto';
    const next = value.slice(0, s) + before + sel + after + value.slice(e);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(s + before.length, s + before.length + sel.length);
    });
  };
  const line = (prefix) => {
    const el = ref.current;
    const s = el ? el.selectionStart : value.length;
    const start = value.lastIndexOf('\n', s - 1) + 1;
    const next = value.slice(0, start) + prefix + value.slice(start);
    onChange(next);
    requestAnimationFrame(() => el && el.focus());
  };
  const insertLink = () => {
    const el = ref.current;
    if (!el) return;
    const { selectionStart: s, selectionEnd: e } = el;
    const sel = value.slice(s, e) || 'texto do link';
    const next = value.slice(0, s) + `[${sel}](https://)` + value.slice(e);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(s + sel.length + 3, s + sel.length + 11);
    });
  };
  const tools = [
    { icon: Bold, label: 'Negrito', fn: () => wrap('**'), id: 'bold' },
    { icon: Italic, label: 'Itálico', fn: () => wrap('*'), id: 'italic' },
    { icon: Heading2, label: 'Título', fn: () => line('## '), id: 'h2' },
    { icon: Heading3, label: 'Subtítulo', fn: () => line('### '), id: 'h3' },
    { icon: List, label: 'Lista', fn: () => line('- '), id: 'list' },
    { icon: Link2, label: 'Link', fn: insertLink, id: 'link' },
    { icon: Minus, label: 'Separador de secção', fn: () => line('\n---\n'), id: 'hr' },
  ];
  return (
    <div data-testid={testId}>
      <p className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</p>
      <div className="border border-zinc-800 bg-black focus-within:border-fl-yellow">
        <div className="flex flex-wrap gap-1 border-b border-zinc-800 p-1.5">
          {tools.map((tl) => (
            <button
              key={tl.id}
              type="button"
              onClick={tl.fn}
              title={tl.label}
              aria-label={tl.label}
              data-testid={`${testId}-${tl.id}`}
              className="flex h-8 w-8 items-center justify-center text-zinc-400 transition-colors hover:bg-zinc-900 hover:text-fl-yellow"
            >
              <tl.icon className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
        <textarea
          ref={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          data-testid={`${testId}-input`}
          className="w-full resize-y bg-transparent px-4 py-3 text-sm text-white placeholder-zinc-700 outline-none"
          placeholder="Escreve aqui... (suporta **negrito**, *itálico*, ## títulos, - listas, [links](url))"
        />
      </div>
    </div>
  );
}
